/**
 * useJoeyLogin — Joey (WalletConnect) sign-in flow (multi-wallet Phase 4).
 *
 * Pair over WalletConnect (QR), then prove address ownership via the
 * challenge-signature flow: POST /auth/challenge → wallet signs a no-op AccountSet
 * (Joey broadcasts it — expected, harmless) → POST /auth/verify-signature → session.
 */

import { useRef, useState } from 'react';
import QRCode from 'qrcode';
import { connectWc, wcSignTransaction, disconnectWc } from '../lib/wallet/wcAdapter';
import { apiClient } from '../api/client';
import { getLinkedWallets } from '../api/auth';
import { useAuthStore } from '../store/authStore';
import { useNotificationStore } from '../store/notificationStore';
import { getReferralCode, clearReferralCode } from '../utils/referral';

export type JoeyLoginStage = 'idle' | 'pairing' | 'authenticating';

export function useJoeyLogin() {
  const [stage, setStage] = useState<JoeyLoginStage>('idle');
  const [pairingQr, setPairingQr] = useState<string | null>(null);
  const [pairingUri, setPairingUri] = useState<string | null>(null);
  const { setUser, setError, setLinkedWallets } = useAuthStore();
  const { error: showError, success: showSuccess } = useNotificationStore();

  // Flips true if the user dismisses the modal mid-flow. The WalletConnect steps are all
  // awaits, so we check it after each one and bail — otherwise approving in the wallet
  // AFTER dismissing would still complete the login behind a closed modal.
  const cancelledRef = useRef(false);
  // Teardown from a previous cancel() is async; hold its promise so a fast re-launch waits
  // for it before connectWc() runs its existing-session check (else it can latch onto a
  // session that's mid-disconnect and later fail with "No active WalletConnect session").
  const disconnectingRef = useRef<Promise<void> | null>(null);

  const launch = async () => {
    cancelledRef.current = false;
    if (disconnectingRef.current) {
      await disconnectingRef.current.catch(() => {});
      disconnectingRef.current = null;
    }
    setStage('pairing');
    setPairingQr(null);
    setPairingUri(null);
    try {
      const session = await connectWc((uri) => {
        setPairingUri(uri); // mobile deep link (WalletConnect pairing URI)
        QRCode.toDataURL(uri, { width: 440, margin: 1 })
          .then(setPairingQr)
          .catch(() => setPairingQr(null));
      });
      if (cancelledRef.current) return;

      setStage('authenticating');
      const challenge = await apiClient.post<any>('/auth/challenge', { address: session.address });
      if (!challenge?.success) throw new Error('Failed to create sign-in challenge');
      if (cancelledRef.current) return;

      const signed = await wcSignTransaction(challenge.data.tx_json);
      if (cancelledRef.current) return;

      const referralCode = getReferralCode() || undefined;
      const verified = await apiClient.post<any>('/auth/verify-signature', {
        address: session.address,
        provider: 'joey-wc',
        ...(referralCode ? { referral_code: referralCode } : {}),
        ...(signed.signedTxJson ? { signed_tx_json: signed.signedTxJson } : {}),
      });
      if (!verified?.success) throw new Error('Sign-in verification failed');
      if (cancelledRef.current) return;

      setUser(verified.data.user, verified.data.token, 'joey-wc');
      clearReferralCode();
      showSuccess('Connected with Joey');

      // Hydrate linked wallets so the wallet switcher is populated (parity with Xaman).
      try {
        const wallets = await getLinkedWallets();
        setLinkedWallets(wallets);
      } catch {
        /* non-fatal — the switcher just stays empty until next load */
      }

      // On the home page, refresh so the logged-in view renders (parity with Xaman).
      if (window.location.pathname === '/') {
        setTimeout(() => window.location.reload(), 500);
      }
    } catch (err) {
      if (cancelledRef.current) return;
      const message = err instanceof Error ? err.message : 'Failed to connect Joey wallet';
      setError(message);
      showError(message);
    } finally {
      setStage('idle');
      setPairingQr(null);
      setPairingUri(null);
    }
  };

  const cancel = () => {
    cancelledRef.current = true;
    setStage('idle');
    setPairingQr(null);
    setPairingUri(null);
    // Tear down the half-open WalletConnect session so a later approval can't resurrect it.
    // Keep the promise so a re-launch can await it (see launch()).
    disconnectingRef.current = disconnectWc().catch(() => {});
  };

  return { stage, pairingQr, pairingUri, launch, cancel };
}
